import { FeedItemType } from '../enums';

/**
 * Conceptual explanation attached to every feed item from the user perspective.
 * Answers: “Why does this appear?” — no UI or implementation.
 */
export type FeedExplanationSignal =
  | 'recency'
  | 'new_creator_boost'
  | 'engagement'
  | 'follow_relationship'
  | 'monetization_weight'
  | 'category_match'
  | 'similar_creator'
  | 'product_relevance'
  | 'inactive_decay_inverse';

export type FeedExplanationRelationship =
  | 'none'
  | 'you_follow_this_creator'
  | 'creator_in_your_category_interest'
  | 'similar_to_creator_you_follow'
  | 'you_purchased_from_creator'
  | 'platform_discovery_boost';

export interface FeedExplanationModel {
  readonly itemId: string;
  readonly itemType: FeedItemType;
  readonly whyShown: string;
  readonly triggeredSignal: FeedExplanationSignal;
  readonly relationship: FeedExplanationRelationship;
}

/** Template patterns for explaining feed items (product copy layer). */
export const FEED_EXPLANATION_TEMPLATES: Readonly<
  Record<FeedExplanationSignal, string>
> = {
  recency: 'Shown because it was published recently.',
  new_creator_boost: 'Shown to help you discover a new creator on Loamify.',
  engagement: 'Shown because this creator or post is receiving strong engagement.',
  follow_relationship: 'Shown because you follow this creator.',
  monetization_weight: 'Shown because this creator is actively monetizing on the platform.',
  category_match: 'Shown because it matches a category you browse or engage with.',
  similar_creator: 'Shown because this creator is similar to someone you follow.',
  product_relevance: 'Shown because this product is relevant to creators you engage with.',
  inactive_decay_inverse: 'Shown because this creator remains active and publishing.',
};

/** Example conceptual explanations per feed item type (reference for product/design). */
export const FEED_EXPLANATION_EXAMPLES: readonly FeedExplanationModel[] = [
  {
    itemId: 'example-post',
    itemType: FeedItemType.POST,
    whyShown: 'You follow this creator and they published recently.',
    triggeredSignal: 'follow_relationship',
    relationship: 'you_follow_this_creator',
  },
  {
    itemId: 'example-creator',
    itemType: FeedItemType.CREATOR_PROFILE,
    whyShown: 'This is a new creator Loamify is helping audiences discover.',
    triggeredSignal: 'new_creator_boost',
    relationship: 'platform_discovery_boost',
  },
  {
    itemId: 'example-product',
    itemType: FeedItemType.PRODUCT,
    whyShown: 'This creator is monetizing and their product matches your interests.',
    triggeredSignal: 'monetization_weight',
    relationship: 'creator_in_your_category_interest',
  },
];
